import { zodResolver } from "@hookform/resolvers/zod";
import {
  INVITABLE_ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  canManageRole,
  createInviteSchema,
  roleHas,
  type CreateInviteInput,
  type InviteDto,
  type MemberDto,
  type WorkspaceRole,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MailPlus, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { formatDate, initials } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/membros")({
  component: MembrosPage,
});

const INVITE_STATUS: Record<
  InviteDto["status"],
  { label: string; variant: "warning" | "success" | "secondary" | "destructive" }
> = {
  pendente: { label: "Pendente", variant: "warning" },
  aceito: { label: "Aceito", variant: "success" },
  revogado: { label: "Cancelado", variant: "secondary" },
  expirado: { label: "Expirado", variant: "destructive" },
};

function MembrosPage() {
  const { workspaceId } = Route.useParams();
  const workspace = useQuery(queries.workspace(workspaceId));
  const members = useQuery(queries.members(workspaceId));
  const me = useQuery(queries.me());
  const [convidarAberto, setConvidarAberto] = useState(false);

  const myRole = workspace.data?.role;
  const podeConvidar = myRole ? roleHas(myRole, "members:invite") : false;

  return (
    <>
      <PageHeader
        title="Membros"
        description="Quem acessa este workspace e com qual papel."
        actions={
          podeConvidar ? (
            <Button onClick={() => setConvidarAberto(true)}>
              <MailPlus />
              Convidar
            </Button>
          ) : null
        }
      />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Equipe</CardTitle>
            <CardDescription>Membros ativos no workspace.</CardDescription>
          </CardHeader>
          <CardContent>
            {members.isPending ? (
              <LoadingState />
            ) : members.isError ? (
              <ErrorState error={members.error} onRetry={() => void members.refetch()} />
            ) : members.data.length === 0 ? (
              <EmptyState
                title="Nenhum membro"
                description="Isso não deveria acontecer: você é o dono."
              />
            ) : (
              <ul className="divide-y">
                {members.data.map((m) => (
                  <MemberRow
                    key={m.id}
                    member={m}
                    workspaceId={workspaceId}
                    myRole={myRole}
                    myUserId={me.data?.id}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {podeConvidar || (myRole && roleHas(myRole, "members:read")) ? (
          <ConvitesCard workspaceId={workspaceId} podeGerenciar={podeConvidar} />
        ) : null}
      </div>

      {myRole ? (
        <ConvidarDialog
          open={convidarAberto}
          onOpenChange={setConvidarAberto}
          workspaceId={workspaceId}
          myRole={myRole}
        />
      ) : null}
    </>
  );
}

function MemberRow({
  member,
  workspaceId,
  myRole,
  myUserId,
}: {
  member: MemberDto;
  workspaceId: string;
  myRole: WorkspaceRole | undefined;
  myUserId: string | undefined;
}) {
  const queryClient = useQueryClient();
  const isMe = member.userId === myUserId;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId] });

  const mudarPapel = useMutation({
    mutationFn: (role: WorkspaceRole) =>
      api(`/api/workspaces/${workspaceId}/members/${member.id}`, {
        method: "PATCH",
        body: { role },
      }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Papel atualizado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remover = useMutation({
    mutationFn: () =>
      api(`/api/workspaces/${workspaceId}/members/${member.id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await invalidate();
      toast.success(isMe ? "Você saiu do workspace." : "Membro removido.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const podeGerenciar =
    !isMe &&
    myRole !== undefined &&
    roleHas(myRole, "members:update_role") &&
    canManageRole(myRole, member.role);
  const papeisDisponiveis = myRole ? INVITABLE_ROLES.filter((r) => canManageRole(myRole, r)) : [];

  return (
    <li className="flex items-center gap-3 py-3">
      <Avatar>
        {member.image ? <AvatarImage src={member.image} alt="" /> : null}
        <AvatarFallback>{initials(member.name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {member.name} {isMe ? <span className="text-muted-foreground">(você)</span> : null}
        </p>
        <p className="truncate text-xs text-muted-foreground">{member.email}</p>
      </div>
      <Badge variant={member.role === "dono" ? "default" : "secondary"}>
        {ROLE_LABELS[member.role]}
      </Badge>
      {podeGerenciar ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Ações para ${member.name}`}
              loading={mudarPapel.isPending || remover.isPending}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Mudar papel</DropdownMenuLabel>
            {papeisDisponiveis.map((r) => (
              <DropdownMenuItem
                key={r}
                disabled={r === member.role}
                onSelect={() => mudarPapel.mutate(r)}
              >
                {ROLE_LABELS[r]}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => remover.mutate()}>
              Remover do workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : isMe && member.role !== "dono" ? (
        <Button
          variant="ghost"
          size="sm"
          loading={remover.isPending}
          onClick={() => remover.mutate()}
        >
          Sair
        </Button>
      ) : null}
    </li>
  );
}

function ConvitesCard({
  workspaceId,
  podeGerenciar,
}: {
  workspaceId: string;
  podeGerenciar: boolean;
}) {
  const invites = useQuery(queries.invites(workspaceId));
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "invites"] });

  const reenviar = useMutation({
    mutationFn: (id: string) =>
      api(`/api/workspaces/${workspaceId}/invites/${id}/reenviar`, { method: "POST" }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Convite reenviado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const revogar = useMutation({
    mutationFn: (id: string) =>
      api(`/api/workspaces/${workspaceId}/invites/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Convite cancelado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Convites</CardTitle>
        <CardDescription>Convites enviados por e-mail. Valem por 7 dias.</CardDescription>
      </CardHeader>
      <CardContent>
        {invites.isPending ? (
          <LoadingState rows={2} />
        ) : invites.isError ? (
          <ErrorState error={invites.error} onRetry={() => void invites.refetch()} />
        ) : invites.data.length === 0 ? (
          <EmptyState
            title="Nenhum convite enviado"
            description="Convide sua equipe pelo botão acima."
          />
        ) : (
          <ul className="divide-y">
            {invites.data.map((inv) => {
              const status = INVITE_STATUS[inv.status];
              return (
                <li key={inv.id} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{inv.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {ROLE_LABELS[inv.role]} · enviado em {formatDate(inv.createdAt)}
                      {inv.invitedByName ? ` por ${inv.invitedByName}` : ""}
                    </p>
                  </div>
                  <Badge variant={status.variant}>{status.label}</Badge>
                  {podeGerenciar && inv.status !== "aceito" ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Ações para o convite de ${inv.email}`}
                          loading={reenviar.isPending || revogar.isPending}
                        >
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => reenviar.mutate(inv.id)}>
                          Reenviar
                        </DropdownMenuItem>
                        {inv.status === "pendente" ? (
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => revogar.mutate(inv.id)}
                          >
                            Cancelar convite
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ConvidarDialog({
  open,
  onOpenChange,
  workspaceId,
  myRole,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  myRole: WorkspaceRole;
}) {
  const queryClient = useQueryClient();
  const form = useForm<CreateInviteInput>({
    resolver: zodResolver(createInviteSchema),
    defaultValues: { email: "", role: "gestor" },
  });
  const papeis = INVITABLE_ROLES.filter((r) => canManageRole(myRole, r));

  const convidar = useMutation({
    mutationFn: (input: CreateInviteInput) =>
      api<InviteDto>(`/api/workspaces/${workspaceId}/invites`, { method: "POST", body: input }),
    onSuccess: async (inv) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "invites"] });
      toast.success(`Convite enviado para ${inv.email}.`);
      form.reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const role = useWatch({ control: form.control, name: "role" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => convidar.mutate(v))}
          noValidate
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Convidar para o workspace</DialogTitle>
            <DialogDescription>
              A pessoa recebe um e-mail com um link que vale por 7 dias.
            </DialogDescription>
          </DialogHeader>
          <FormField id="invite-email" label="E-mail" error={form.formState.errors.email?.message}>
            <Input
              id="invite-email"
              type="email"
              placeholder="pessoa@agencia.com.br"
              autoFocus
              {...form.register("email")}
            />
          </FormField>
          <FormField
            id="invite-role"
            label="Papel"
            error={form.formState.errors.role?.message}
            hint={ROLE_DESCRIPTIONS[role]}
          >
            <Select
              value={role}
              onValueChange={(v) =>
                form.setValue("role", v as CreateInviteInput["role"], { shouldValidate: true })
              }
            >
              <SelectTrigger id="invite-role">
                <SelectValue placeholder="Escolha um papel" />
              </SelectTrigger>
              <SelectContent>
                {papeis.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={convidar.isPending}>
              Enviar convite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
